import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $getNodeByKey,
  LexicalEditor,
  LexicalNode,
} from 'lexical';
import {
  $wrapSelectionInMarkNode,
  $unwrapMarkNode,
  $isMarkNode,
  MarkNode,
} from '@lexical/mark';
import {
  Annotation,
  HIGHLIGHT_COLORS,
} from '../../../../../../TextAnnotation/types';
import { FloatingAnnotationButton } from './FloatingAnnotationButton';

const generateAnnotationId = () => crypto.randomUUID();

function $findMarkNodes(
  node: LexicalNode,
  annotationId: string,
  result: MarkNode[],
) {
  if (node instanceof MarkNode && node.getIDs().includes(annotationId)) {
    result.push(node);
  }
  if ($isElementNode(node)) {
    for (const child of node.getChildren()) {
      $findMarkNodes(child, annotationId, result);
    }
  }
}

export function removeAnnotationMarks(
  editor: LexicalEditor,
  annotationId: string,
) {
  editor.update(() => {
    const marks: MarkNode[] = [];
    $findMarkNodes($getRoot(), annotationId, marks);
    for (const mark of marks) {
      mark.deleteID(annotationId);
      if (mark.getIDs().length === 0) {
        $unwrapMarkNode(mark);
      }
    }
  });
}

type AnnotationPluginProps = {
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  onRequestDrawerOpen: () => void;
  isEditing: boolean;
  canAnnotate?: boolean;
  editorRef?: React.MutableRefObject<LexicalEditor | null>;
};

export const AnnotationPlugin: React.FC<AnnotationPluginProps> = ({
  annotations,
  onAnnotationsChange,
  onRequestDrawerOpen,
  isEditing,
  canAnnotate,
  editorRef,
}) => {
  const [editor] = useLexicalComposerContext();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
    return () => {
      if (editorRef) {
        editorRef.current = null;
      }
    };
  }, [editor, editorRef]);

  const ANCHOR_CLASS = 'MarkdownEditor__annotationAnchor';

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const container = rootElement.parentElement;
    if (!container) return;

    container.classList.add(ANCHOR_CLASS);
    setAnchorElement(container);

    return () => {
      container.classList.remove(ANCHOR_CLASS);
    };
  }, [editor]);

  useEffect(() => {
    function applyColorToMark(element: HTMLElement, ids: string[]) {
      const ann = annotationsRef.current.find(a => ids.includes(a.id));
      if (ann) {
        const color =
          HIGHLIGHT_COLORS[ann.colorIndex % HIGHLIGHT_COLORS.length];
        element.style.backgroundColor = color.bg;
        element.style.borderBottom = `2px solid ${color.border}`;
        element.style.paddingBottom = '1px';
      }
    }

    return editor.registerMutationListener(MarkNode, mutations => {
      editor.getEditorState().read(() => {
        for (const [nodeKey, mutation] of mutations) {
          if (mutation === 'destroyed') continue;
          const node = $getNodeByKey(nodeKey);
          if (!$isMarkNode(node)) continue;
          const element = editor.getElementByKey(nodeKey);
          if (element) {
            applyColorToMark(element, node.getIDs());
          }
        }
      });
    });
  }, [editor]);

  const handleAddAnnotation = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        return;
      }

      const selectedText = selection.getTextContent();
      const id = generateAnnotationId();
      const colorIndex =
        annotationsRef.current.length % HIGHLIGHT_COLORS.length;

      $wrapSelectionInMarkNode(selection, selection.isBackward(), id);

      const newAnnotation: Annotation = {
        id,
        selectedText,
        comment: '',
        colorIndex,
      };

      // Styling is applied by the registerMutationListener above once
      // Lexical reconciles the new MarkNodes to the DOM.
      onAnnotationsChange([...annotationsRef.current, newAnnotation]);
      onRequestDrawerOpen();
    });
  }, [editor, onAnnotationsChange, onRequestDrawerOpen]);

  if ((!isEditing && !canAnnotate) || !anchorElement) {
    return null;
  }

  return (
    <FloatingAnnotationButton
      anchorElement={anchorElement}
      onAddAnnotation={handleAddAnnotation}
    />
  );
};
